import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, mergeClasses, Option, Radio, RadioGroup } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useCommonStyles } from '../../../common/hooks';
import { WinnerUploadedMediaDownloadCategoryType } from '../../../common/types/winner-uploaded-media-download-category-types';
import { Group, ProgramRegion } from '../../../models';
import { CustomMessageBar } from '../CustomMessageBar';
import { SelectOptions } from '../form/SelectOptions';

interface AllMediaDownloadModalProps {
  groups: Group[];
  regions: ProgramRegion[];
  onCloseModal(): void;
  onDownloadAll(): void;
  onDownloadByGroupRegion(downloadCategoryType: WinnerUploadedMediaDownloadCategoryType, ids: number[]): void;
}

const AllMediaDownloadModalImpl: React.FC<AllMediaDownloadModalProps> = (props) => {
  const {
    groups,
    regions,
    onCloseModal,
    onDownloadAll,
    onDownloadByGroupRegion,
  } = props

  const commonStyles = useCommonStyles();

  const [isDownloadAll, setDownloadAll] = useState(true);

  const [selectedRegionIds, setSelectedRegionIds] = useState<number[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

  const isSelectGroupsDisabled = (): boolean => {
    return selectedRegionIds.length > 0;
  }

  const isSelectRegionsDisabled = (): boolean => {
    return selectedGroupIds.length > 0;
  }

  const onGroupCheckedChange = (selectedValue: string[]): void => {
    const _selectedGroupIds = selectedValue.map((value) => +value);
    setSelectedGroupIds(_selectedGroupIds);
  };

  const onRegionCheckedChange = (selectedValue: string[]): void => {
    const _selectedRegionIds = selectedValue.map((value) => +value);
    setSelectedRegionIds(_selectedRegionIds);
  };

  const onStartDownloadClicked = () => {
    if (isDownloadAll) {
      onDownloadAll();
      return;
    }

    const downloadCategoryType: WinnerUploadedMediaDownloadCategoryType = selectedGroupIds.length > 0 ? 'group' : 'region';
    const ids = selectedGroupIds.length > 0 ?
      selectedGroupIds :
      selectedRegionIds;

    onDownloadByGroupRegion(downloadCategoryType, ids);
  };

  useEffect(() => {
    setSelectedGroupIds([]);
    setSelectedRegionIds([]);
  }, [isDownloadAll]);

  return (
    <Dialog
      open
      onOpenChange={(_, data) => {
        if (!data.open) onCloseModal();
      }}
    >
      <DialogSurface className={mergeClasses('all-media-download-modal', commonStyles.modalSurface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={(
              <Button
                appearance='transparent'
                title='Close'
                icon={<DismissRegular />}
                onClick={onCloseModal}
              />
            )}
          >
            Download media
          </DialogTitle>

          <DialogContent>
            <RadioGroup
              aria-label='Download media selector'
              value={isDownloadAll ? 'downloadAll' : 'selectGroups'}
              onChange={(_, data) => setDownloadAll(data.value === 'downloadAll')}
            >
              <Radio value='downloadAll' label='Download all' />
              <Radio value='selectGroups' label='Select groups / regions' />
            </RadioGroup>
            {
              isDownloadAll ? (
                <CustomMessageBar
                  notificationTypeCode='info'
                  message='Download may take up to 2 hours. Download all Winner/Finalist Media, or select specific groups and regions. Do not close your browser until download is complete.'
                />
              ) : (
                <>
                  <SelectOptions
                    label='Select groups'
                    onOptionSelect={(data) => onGroupCheckedChange(data.selectedOptions)}
                    dropdownProps={{
                      disabled: isSelectGroupsDisabled(),
                      multiselect: true,
                    }}
                  >
                    {
                      groups.map((group) => (
                        <Option key={group.groupId} value={`${group.groupId}`}>{group.displayName}</Option>
                      ))
                    }
                  </SelectOptions>
                  <SelectOptions
                    label='Select regions'
                    onOptionSelect={(data) => onRegionCheckedChange(data.selectedOptions)}
                    dropdownProps={{
                      disabled: isSelectRegionsDisabled(),
                      multiselect: true,
                    }}
                  >
                    {
                      regions.map((region) => (
                        <Option key={region.regionId} value={`${region.regionId}`}>{region.displayName}</Option>
                      ))
                    }
                  </SelectOptions>
                </>
              )
            }
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={() => onCloseModal()}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              onClick={() => onStartDownloadClicked()}
            >
              Start
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export const AllMediaDownloadModal = observer(AllMediaDownloadModalImpl);
